import { getCurrentEnvironment } from '@mobi/utils/environment'

export enum DepositUrl {
  CreatePaymentMethod = '/$_/accountDeposit/payment-methods/createPaymentMethod',
  SetDefaultPaymentMethod = '/$_/accountDeposit/payment-methods/setDefault',
  DeletePaymentMethod = '/$_/accountDeposit/payment-methods/deletePaymentMethod',
  InitialData = '/$_/accountDeposit/payment-methods/initialData',
  GeneratePaymentNonce = '/$_/accountDeposit/payment-methods/generatePaymentMethodNonce',
  Deposit = '/$_/accountDeposit/payment-methods/deposit',
}

export const WithdrawUrl = {
  withdrawDetails: (accountNumber: number) => `/api/bet-account/${accountNumber}/withdraw-details`,
  withdraw: (accountNumber: number) => `/api/bet-account/${accountNumber}/withdraw`,
  withdraw_legacy: '/api/account/withdraw',
  createZeptoContact: '/api/eft-mgt/contact/create',
  deleteZeptoContact: '/api/eft-mgt/contact',
  getBsbDetails: (bsb: string) => `/api/bet-account/bank-details?bsb=${bsb}`,
  sendOtp: (accountNumber: number) => `/api/bet-account/${accountNumber}/otp`,
}

export const SignUpUrl = {
  signUpApplication: '/api/signup/SignupApplication',
  verifyDriversLicence: '/api/bet-account/verification/drivers-licence',
  verifyMedicare: '/api/bet-account/verification/medicare',
  verifyPassport: '/api/bet-account/verification/passport',
  updateDepositLimitAnniversary:
    '/$_/api/account/updateDepositLimitAnniversary' /** @deprecated Use saveDepositLimitAndAnniversary instead */,
  saveDepositLimitAndAnniversary: (accountNumber: number) =>
    `/api/bet-account/${accountNumber}/deposit-limit`,
  countryList: '/$_/api/SignUp/GetCountryList',
  createBetAccount: '/api/bet-account/signup-application/wagering-account',
}

const getContactVerificationApiUrl = () => {
  const env = getCurrentEnvironment()[0]
  return `https://contactverification-api.${env}.tabtouch.com.au`
}
export const PersonalDetailsUrls = {
  editContactDetails: (accountNumber: number, betAccountHolderNumber: number | null) =>
    `/api/bet-account/${accountNumber}/contact/${betAccountHolderNumber}/details`,
  suburbList: (searchString: string) =>
    `/api/bet-account/address/suburbs?searchString=${encodeURIComponent(searchString)}`,
  streeTypeList: (searchString: string) =>
    `/api/bet-account/address/street-types?searchString=${encodeURIComponent(searchString)}`,
  splitAddress: () => `${getContactVerificationApiUrl()}/v1/address/split`,
  getAutoAddressList: (search: string, limit: string) =>
    `${getContactVerificationApiUrl()}/v1/address/search?query=${encodeURIComponent(search)}&limit=${limit}`,
}

export const accountUrl = {
  savePrecommitments: '/$_/api/account/updatePrecommitments',
}

type AccountUrls = DepositUrl | keyof typeof WithdrawUrl | keyof typeof SignUpUrl

type Parser<TResponse> = (json: string) => TResponse

export class ApiServiceError extends Error {
  response: Response

  constructor(response: Response) {
    super(`Fetch failed. Error code ${response.status}`)
    this.response = response
  }
}

const defaultParser = <TResponse>(json: string) => json as TResponse

export const get = async <TResponse = string>(
  url: AccountUrls | string,
  parser: Parser<TResponse> = defaultParser
): Promise<TResponse> => {
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
  })
  if (response.ok) {
    const json = await response.json()
    return parser(json)
  } else {
    throw { status: response.status, statusText: response.statusText }
  }
}

export const post = async <TRequest, TResponse = string>(
  url: AccountUrls | string,
  body: TRequest,
  parser: Parser<TResponse> = defaultParser
) => {
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    credentials: 'include',
    headers: JSONContentHeaders,
  })
  if (response.ok) {
    if (response.status === 204) {
      return undefined as TResponse
    }

    const json = await response.json()
    return parser(json)
  } else {
    throw { status: response.status, statusText: response.statusText }
  }
}

export const put = async <TRequest, TResponse = string>(
  url: AccountUrls | string,
  body: TRequest,
  parser: Parser<TResponse> = defaultParser
) => {
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    credentials: 'include',
    headers: JSONContentHeaders,
  })

  if (response.ok) {
    if (response.status === 204) return null

    const json = await response.json()
    return parser(json)
  } else {
    const contentType = response.headers.get('content-type')
    const error: { status: number; statusText: string; body?: unknown } = {
      status: response.status,
      statusText: response.statusText,
    }

    if (contentType?.includes('application/json')) {
      error.body = await response.json()
    }

    throw error
  }
}

export const patch = async <TRequest, TResponse = string>(
  url: AccountUrls | string,
  body: TRequest,
  parser: Parser<TResponse> = defaultParser
) => {
  const response = await fetch(url, {
    method: 'PATCH',
    body: JSON.stringify(body),
    credentials: 'include',
    headers: JSONContentHeaders,
  })
  if (response.ok) {
    if (response.status === 204) {
      return undefined as TResponse
    }

    const json = await response.json()
    return parser(json)
  } else {
    throw { status: response.status, statusText: response.statusText }
  }
}

export const del = async <TRequest, TResponse = string>(
  url: AccountUrls | string,
  body: TRequest,
  parser: Parser<TResponse> = defaultParser
) => {
  const response = await fetch(url, {
    method: 'DELETE',
    body: JSON.stringify(body),
    credentials: 'include',
    headers: JSONContentHeaders,
  })

  if (response.ok) {
    if (response.status === 204) return null

    const json = await response.json()
    return parser(json)
  } else {
    throw { status: response.status, statusText: response.statusText }
  }
}

/**
 * Constants
 */

const JSONContentHeaders = {
  'Content-type': 'application/json; charset=UTF-8',
  Accept: 'application/json',
}
